// File: src/components/general/Chip/Chip.tsx
// Purpose: General UI component or utility

import React from "react";
import styled from "styled-components";
import { X, Check } from "lucide-react";
import { themeUtils } from "../../../theme";

export interface ChipProps {
  label: string;
  color?: "primary" | "secondary" | "success" | "warning" | "error" | "info" | "default" | "neutral";
  selectable?: boolean;
  selected?: boolean;
  size?: "xsmall" | "small" | "medium" | "large";
  icon?: React.ReactNode;
  avatarUrl?: string;
  onClick?: () => void;
  onRemove?: () => void;
}

// ✅ Styled Chip Component
const ChipWrapper = styled.div<{ color: string; selected: boolean; size: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ size, theme }) =>
    size === "xsmall" ? `${theme.spacing.xxs} ${theme.spacing.xs}` :
    size === "small" ? `${theme.spacing.xs} ${theme.spacing.sm}` :
    size === "large" ? `${theme.spacing.md} ${theme.spacing.lg}` :
    `${theme.spacing.sm} ${theme.spacing.md}`};
  font-size: ${({ size, theme }) =>
    size === "xsmall" ? theme.typography.overline.fontSize :
    size === "small" ? theme.typography.bodySmall.fontSize :
    size === "large" ? theme.typography.heading3.fontSize :
    theme.typography.body.fontSize};
  font-weight: ${({ theme }) => theme.typography.body.fontWeight};
  line-height: ${({ theme }) => theme.typography.body.lineHeight};
  border-radius: ${({ theme }) => theme.radii.md};
  white-space: nowrap;
  cursor: ${({ onClick }) => (onClick ? "pointer" : "default")};
  background-color: ${({ color, selected, theme }) =>
  selected
    ? themeUtils.getColorVariant(color, "Selected", theme.colors)
    : theme.colors[color as keyof typeof theme.colors]};

  background-color: ${({ color, selected, theme }) =>
    selected ? theme.colors[`${color}Selected` as keyof typeof theme.colors] : theme.colors[color as keyof typeof theme.colors]};
  color: ${({ selected, theme }) =>
    selected ? theme.colors.textPrimary : theme.colors.textPrimary};
  border: ${({ theme }) => theme.border.md} solid ${({ color, theme }) =>  themeUtils.getColorVariant(color, "Hover", theme.colors)};
  transition: ${({ theme }) => theme.transitions.fast};
  user-select: none;
  max-height:30px;
  &:hover {
  background-color: ${({ color, theme }) =>
    themeUtils.getColorVariant(color, "Hover", theme.colors)};
}
`;

// ✅ Styled Remove Button
const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  margin-left: ${({ theme }) => theme.spacing.xs};
  padding: 0;
  cursor: pointer;
  color: inherit;
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

// ✅ Styled Avatar (optional)
const Avatar = styled.img`
  width: 20px;
  height: 20px;
  border-radius: ${({ theme }) => theme.radii.rounded};
  object-fit: cover;
`;

// ✅ Main Chip Component
export const Chip: React.FC<ChipProps> = ({
  label,
  color = "default",
  selectable = false,
  selected = false,
  size = "small",
  icon,
  avatarUrl,
  onClick,
  onRemove,
}) => {
  return (
    <ChipWrapper color={color} selected={selected} size={size} onClick={selectable ? onClick : undefined}>
      {avatarUrl && <Avatar src={avatarUrl} alt="avatar" />}
      {icon && icon}
      {label}
      {selectable && selected && <Check size={14} />}
      {onRemove && (
        <RemoveButton onClick={onRemove} aria-label="Remove chip">
          <X size={14} />
        </RemoveButton>
      )}
    </ChipWrapper>
  );
};